import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2, Shield, Zap } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionLabel from "@/components/site/SectionLabel";
import { post } from "@/lib/api";

const fmtMoney = (v) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(v || 0);

const PACKAGES = [
    {
        id: "pay_in_full",
        label: "Pay In Full",
        eyebrow: "Best Value",
        savings: 148,
        amountNow: 2999,
        total: 2999,
        sub: "Single payment. Done.",
        bullets: [
            "Save $148 vs the installment plan",
            "Lifetime access to all 90-day curriculum",
            "Full coaching access from day 1",
            "No recurring charges to manage",
        ],
        highlight: true,
    },
    {
        id: "plan_3",
        label: "3-Payment Plan",
        eyebrow: "Flexible",
        savings: 0,
        amountNow: 1049,
        total: 3147,
        sub: "Spread the cost across 3 months",
        bullets: [
            "Pay $1,049 today",
            "Two follow-up installments of $1,049",
            "Same program. Same access from day 1.",
            "Cancel-free, no hidden interest",
        ],
        highlight: false,
    },
];

export default function Pricing() {
    const [params] = useSearchParams();
    const [loadingId, setLoadingId] = useState(null);
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
    });

    useEffect(() => {
        document.title = "Enroll | 90 Day Alpha";
    }, []);

    useEffect(() => {
        if (params.get("cancelled") === "1") {
            toast.error("Checkout cancelled. Pick a plan to try again.");
        }
    }, [params]);

    const enroll = async (pkg) => {
        if (!form.customer_email || !form.customer_name) {
            toast.error("Add your name + email so we can attach the program to your account.");
            return;
        }
        try {
            setLoadingId(pkg.id);
            const data = await post("/payments/v1/checkout/session", {
                package_id: pkg.id,
                origin_url: window.location.origin,
                customer_name: form.customer_name.trim(),
                customer_email: form.customer_email.trim(),
                customer_phone: form.customer_phone.trim() || null,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("No checkout URL");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not start checkout. Please try again."
            );
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <div data-testid="pricing-page" className="electric-bg relative overflow-hidden">
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#1E90FF]/10 blur-[160px] pointer-events-none"
            />

            <section className="relative pt-32 pb-10 md:pt-40 md:pb-14">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-center flex flex-col items-center">
                    <Reveal>
                        <SectionLabel testId="pricing-label">
                            Enroll Now
                        </SectionLabel>
                        <h1 className="mt-6 font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
                            Lock in your{" "}
                            <span className="text-[#00B7FF] text-glow-blue">
                                transformation
                            </span>
                            .
                        </h1>
                        <p className="mt-6 text-white/75 leading-relaxed text-base sm:text-lg max-w-2xl">
                            Skip the call and start today, or book a free
                            consultation first. Same program either way.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="relative pb-10">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <div className="bg-[#060c1e] border border-[#1a2a4a] p-6 md:p-7">
                        <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00B7FF]">
                            Your Info
                        </p>
                        <p className="mt-1 text-white/55 text-sm">
                            We'll attach your enrollment to this email + phone.
                        </p>
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Field
                                testId="enroll-name"
                                placeholder="Full Name"
                                value={form.customer_name}
                                onChange={(v) =>
                                    setForm((f) => ({
                                        ...f,
                                        customer_name: v,
                                    }))
                                }
                            />
                            <Field
                                testId="enroll-email"
                                type="email"
                                placeholder="Email"
                                value={form.customer_email}
                                onChange={(v) =>
                                    setForm((f) => ({
                                        ...f,
                                        customer_email: v,
                                    }))
                                }
                            />
                            <Field
                                testId="enroll-phone"
                                type="tel"
                                placeholder="Phone (optional)"
                                value={form.customer_phone}
                                onChange={(v) =>
                                    setForm((f) => ({
                                        ...f,
                                        customer_phone: v,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative pb-16 md:pb-24">
                <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {PACKAGES.map((p) => (
                        <Reveal key={p.id} delay={p.highlight ? 0 : 0.1}>
                            <article
                                data-testid={`package-card-${p.id}`}
                                className={`relative bg-[#060c1e] border ${
                                    p.highlight
                                        ? "border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.18)]"
                                        : "border-[#1a2a4a]"
                                } p-7 md:p-9 h-full flex flex-col`}
                            >
                                {p.highlight && (
                                    <div className="absolute -top-3 left-7 px-2.5 py-1 text-[10px] uppercase tracking-[0.28em] font-bold bg-[#D4AF37] text-black">
                                        {p.eyebrow}
                                    </div>
                                )}
                                {!p.highlight && (
                                    <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00B7FF]">
                                        {p.eyebrow}
                                    </p>
                                )}

                                <h2 className="mt-3 font-display font-black text-white text-3xl tracking-tight">
                                    {p.label}
                                </h2>
                                <p className="mt-1 text-white/55 text-sm">
                                    {p.sub}
                                </p>

                                <div className="mt-6 flex items-baseline gap-3">
                                    <span className="font-display font-black text-white text-5xl tracking-tighter">
                                        {fmtMoney(p.amountNow)}
                                    </span>
                                    {p.installments > 1 && (
                                        <span className="text-white/55 text-xs uppercase tracking-[0.15em]">
                                            / today
                                        </span>
                                    )}
                                </div>
                                {p.id === "plan_3" ? (
                                    <p className="mt-1 text-white/55 text-xs">
                                        Then $1,049/mo × 2 ·{" "}
                                        <span className="text-white">
                                            {fmtMoney(p.total)} total
                                        </span>
                                    </p>
                                ) : (
                                    <p className="mt-1 text-[#00B7FF] text-xs uppercase tracking-[0.18em] font-semibold">
                                        Save {fmtMoney(p.savings)}
                                    </p>
                                )}

                                <ul className="mt-6 space-y-3 flex-1">
                                    {p.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-3 text-white/85 text-sm"
                                        >
                                            <Check
                                                size={16}
                                                className={
                                                    p.highlight
                                                        ? "text-[#D4AF37] mt-0.5"
                                                        : "text-[#00B7FF] mt-0.5"
                                                }
                                                strokeWidth={3}
                                            />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    type="button"
                                    data-testid={`enroll-${p.id}`}
                                    onClick={() => enroll(p)}
                                    disabled={loadingId === p.id}
                                    className={`mt-7 inline-flex items-center justify-center gap-2 w-full px-7 py-4 font-bold uppercase tracking-[0.18em] text-xs transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                                        p.highlight
                                            ? "bg-[#D4AF37] text-black hover:bg-[#C5A059] shadow-[0_0_40px_rgba(212,175,55,0.3)]"
                                            : "bg-transparent text-white border border-[#1a2a4a] hover:border-[#1E90FF] hover:text-[#00B7FF]"
                                    }`}
                                >
                                    {loadingId === p.id ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                            Starting checkout…
                                        </>
                                    ) : (
                                        <>
                                            {p.highlight
                                                ? "Enroll Now"
                                                : "Choose Plan"}
                                            <ArrowUpRight size={14} />
                                        </>
                                    )}
                                </button>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <div className="max-w-3xl mx-auto px-6 md:px-12 mt-10 text-center">
                    <div className="inline-flex items-center gap-2 text-white/55 text-[11px] uppercase tracking-[0.2em] font-semibold">
                        <Shield size={12} className="text-[#00B7FF]" />
                        Secure payment by Stripe · 256-bit SSL · No card stored on
                        our servers
                    </div>
                </div>
            </section>

            <section className="relative pb-24 md:pb-32">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
                    <div className="electric-divider mb-10" />
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Zap
                            size={14}
                            className="text-[#00B7FF]"
                            fill="currentColor"
                        />
                        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                            Not Ready Yet?
                        </p>
                    </div>
                    <h2 className="font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                        Book a free consultation first.
                    </h2>
                    <p className="mt-4 text-white/65 leading-relaxed max-w-xl mx-auto">
                        30 minutes. No pressure. We diagnose where you’re stuck
                        and you decide if 90 Day Alpha is the right fit.
                    </p>
                    <Link
                        to="/book"
                        data-testid="pricing-book-call"
                        className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-4 bg-transparent border border-[#1a2a4a] text-white font-bold uppercase tracking-[0.18em] text-xs hover:border-[#1E90FF] hover:text-[#00B7FF] transition-all"
                    >
                        Book A Free Consultation <ArrowUpRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

function Field({ testId, value, onChange, placeholder, type = "text" }) {
    return (
        <input
            data-testid={testId}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white placeholder-white/30 px-4 py-3 text-sm"
        />
    );
}
